/**
 * Get offline download progress information for a given BookMarker,
 * which includes progress of all child markers.
 *
 * @method getMarkerProgress
 * @param {BookMarker} marker
 * @param {Object} documentsProgress - information about document progress
 * @param {Object} markersProgress - previous map of markers to objects with progress information
 * @returns {Object} object containing progress information
 */
const getMarkerProgress = (marker, documentsProgress, markersProgress) => {
  // Absolute paths for the marker
  let absolutePaths = marker.documents
    .map((document) => documentsProgress.get(document)?.absolutePaths ?? [])
    .flat();

  // Downloaded paths for the marker
  let downloadedAbsolutePaths = marker.documents
    .map(
      (document) =>
        documentsProgress.get(document)?.downloadedAbsolutePaths ?? [],
    )
    .flat();

  // Iterate child markers
  const childMarkersProgress =
    markersProgress.get(marker)?.childMarkerProgress ?? new Map();

  for (let childMarker of marker.children) {
    if (childMarkersProgress.get(childMarker)?.isComplete) {
      continue;
    }

    // Get progress for child marker
    const childMarkerProgress = getMarkerProgress(
      childMarker,
      documentsProgress,
      markersProgress,
    );

    // Collect a flat map containing child marker progress and progress of each of its children, recursive
    childMarkersProgress.set(childMarker, childMarkerProgress);

    for (let [
      childMarker,
      childMarkerProgressNested,
    ] of childMarkerProgress.childMarkersProgress.entries()) {
      childMarkersProgress.set(childMarker, childMarkerProgressNested);
    }

    // Collect child paths
    absolutePaths.push(...childMarkerProgress.absolutePaths);
    downloadedAbsolutePaths.push(
      ...childMarkerProgress.downloadedAbsolutePaths,
    );
  }

  // Ensure paths unique
  absolutePaths = [...new Set(absolutePaths)];
  downloadedAbsolutePaths = [...new Set(downloadedAbsolutePaths)];

  // Ensure all documents are complete
  const allDocumentsComplete = marker.documents.every(
    (document) => documentsProgress.get(document)?.isComplete,
  );

  let percentage = absolutePaths.length
    ? downloadedAbsolutePaths.length / absolutePaths.length
    : 1;

  // If all documents are not complete, prevent the progress from reaching 100%.
  if (!allDocumentsComplete) {
    percentage = Math.min(percentage, 0.99);
  }

  return {
    childMarkersProgress,
    absolutePaths,
    downloadedAbsolutePaths,
    isComplete:
      absolutePaths.length === downloadedAbsolutePaths.length &&
      allDocumentsComplete,
    percentage,
    percentageDisplay: Math.round(percentage * 10000) / 100,
  };
};

export default getMarkerProgress;
